import { Api } from "./api";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  IMultipleResourcesDetailPageResponse,
  IResourceDownloadMailDataResponse,
  IResourcesPagesResponse,
} from "./dtos/resources.type";
import { createInfiniteQuery } from "@/utils/infinite-query.helper";
import { omit } from "lodash";

class Resource extends Api {
  readonly baseUrl = "";

  /**
   * Get all resources details page data
   */
  readonly all = (params: { populate: string }, slug: string) => {
    return this.http.get<IMultipleResourcesDetailPageResponse>(
      this.route(slug),
      {
        params,
      },
    );
  };

  /**
   * Get all resources page data
   */
  readonly getSingleResponse = (
    slug: string,
    params?: { populate: string },
  ) => {
    return this.http.get<IResourcesPagesResponse>(this.route(slug), {
      params,
    });
  };

  /**
   * Get one resources detail page data
   */
  readonly getSingleResponseDetail = (
    id: string,
    params?: {
      filters?: { slug: { $eq: string } };
      populate: string;
    },
  ) => {
    return this.http.get<IMultipleResourcesDetailPageResponse>(this.route(id), {
      params,
    });
  };

  /**
   * Get all resources catagories infinite query data
   */
  readonly getResourceCategoryData = (params: {
    slug: string;
    pagination?: { pageSize: number; page?: number };
    sort: string;
    populate: string;
  }) => {
    return this.http.get<IMultipleResourcesDetailPageResponse>(
      this.route(params.slug),
      {
        params: omit(params, ["slug"]),
      },
    );
  };

  /**
   * Get Resource Download Form Data
   */
  readonly getResourceDownloadMailData = (id: string) => {
    return this.http.get<IResourceDownloadMailDataResponse>(
      this.route(`/resources-forms/${id}`),
      { params: { populate: "deep,2" } },
    );
  };
}

export const resourceApi = new Resource();

export const resourceQuery = createQueryKeys("resources", {
  // Get all resources details page data
  all: (slug: string) => {
    const queryParams = { populate: "deep", sort: "publishedAt:DESC" };

    return {
      queryKey: [slug, queryParams],
      queryFn: async () => {
        const { data } = await resourceApi.all(queryParams, slug);
        return data;
      },
    };
  },

  // Get all resources page data
  getSingleResponse: (slug: string) => {
    const queryParams = { populate: "deep" };

    return {
      queryKey: [slug, queryParams],
      queryFn: async () => {
        const { data } = await resourceApi.getSingleResponse(slug, queryParams);
        return data;
      },
    };
  },

  // Get resources details page data
  getSingleResponseDetail: (slug: string, id: string) => {
    const queryParams = { filters: { slug: { $eq: id } }, populate: "deep,6" };

    return {
      queryKey: [queryParams],
      queryFn: async () => {
        const { data } = await resourceApi.getSingleResponseDetail(
          slug,
          queryParams,
        );
        return data;
      },
    };
  },
});

export const resourcesInfiniteQuery = {
  all: createInfiniteQuery(
    ["_resources", "all"],
    resourceApi.getResourceCategoryData,
  ),
};
